/**
 * Bank accounts an agency can be paid into.
 *
 * The problem this solves: one free-text "bank details" box cannot be laid out on a payment
 * page, cannot be copied field by field, and cannot tell a tenant paying in dollars which of the
 * agency's accounts is the right one. So an account is structured data, and what "structured"
 * means depends on the currency — a naira account needs three fields, a euro account needs an
 * IBAN and a BIC, and a dollar account needs a routing number and the bank's address.
 *
 * The field list per currency is defined here, once, and used by three things: the form that
 * collects an account, the validator that accepts it, and the payment page that displays it.
 * Adding support for a currency is an entry in this table and nothing else — no migration,
 * because the values live in a JSON column keyed by these `key`s.
 */

export interface BankFieldSpec {
  /** Stored key inside the account's `fields` object. Never change one that is in use. */
  key: string;
  label: string;
  required: boolean;
  hint?: string;
  placeholder?: string;
  /** Shown to the browser so a phone offers the numeric keypad. */
  numeric?: boolean;
  minLength?: number;
  maxLength?: number;
  /** Uppercase the value on save — IBANs, BICs and sort codes are conventionally upper case. */
  uppercase?: boolean;
}

const ACCOUNT_NAME: BankFieldSpec = {
  key: "accountName",
  label: "Account name",
  required: true,
  placeholder: "Broadview Estates Ltd",
  hint: "Exactly as the bank has it — a mismatch is the commonest reason a transfer bounces.",
};

const BANK_NAME: BankFieldSpec = {
  key: "bankName",
  label: "Bank name",
  required: true,
  placeholder: "GTBank",
};

/**
 * Per-currency field lists.
 *
 * Deliberately the *minimum* a payer needs, not everything a bank could ask for. Each extra
 * required field is another reason someone abandons a transfer.
 */
const BY_CURRENCY: Record<string, BankFieldSpec[]> = {
  NGN: [
    ACCOUNT_NAME,
    { key: "accountNumber", label: "Account number", required: true, numeric: true, minLength: 10, maxLength: 10, placeholder: "0123456789", hint: "Ten digits." },
    BANK_NAME,
  ],
  GHS: [
    ACCOUNT_NAME,
    { key: "accountNumber", label: "Account number", required: true, numeric: true, maxLength: 20 },
    BANK_NAME,
    { key: "branch", label: "Branch", required: false },
  ],
  KES: [
    ACCOUNT_NAME,
    { key: "accountNumber", label: "Account number", required: true, numeric: true, maxLength: 20 },
    BANK_NAME,
    { key: "branchCode", label: "Branch code", required: false, numeric: true },
  ],
  ZAR: [
    ACCOUNT_NAME,
    { key: "accountNumber", label: "Account number", required: true, numeric: true, maxLength: 20 },
    BANK_NAME,
    { key: "branchCode", label: "Branch code", required: true, numeric: true, hint: "The universal branch code is usually enough." },
  ],
  UGX: [ACCOUNT_NAME, { key: "accountNumber", label: "Account number", required: true, numeric: true }, BANK_NAME, { key: "swift", label: "SWIFT / BIC", required: false, uppercase: true }],
  TZS: [ACCOUNT_NAME, { key: "accountNumber", label: "Account number", required: true, numeric: true }, BANK_NAME, { key: "swift", label: "SWIFT / BIC", required: false, uppercase: true }],
  RWF: [ACCOUNT_NAME, { key: "accountNumber", label: "Account number", required: true, numeric: true }, BANK_NAME],
  XOF: [ACCOUNT_NAME, { key: "iban", label: "IBAN", required: true, uppercase: true, maxLength: 34 }, BANK_NAME],
  XAF: [ACCOUNT_NAME, { key: "iban", label: "IBAN", required: true, uppercase: true, maxLength: 34 }, BANK_NAME],
  EGP: [ACCOUNT_NAME, { key: "accountNumber", label: "Account number", required: true, numeric: true }, BANK_NAME, { key: "swift", label: "SWIFT / BIC", required: false, uppercase: true }],
  MAD: [ACCOUNT_NAME, { key: "rib", label: "RIB", required: true, numeric: true, minLength: 24, maxLength: 24, hint: "Twenty-four digits." }, BANK_NAME],
  GBP: [
    ACCOUNT_NAME,
    { key: "sortCode", label: "Sort code", required: true, numeric: true, minLength: 6, maxLength: 8, placeholder: "12-34-56" },
    { key: "accountNumber", label: "Account number", required: true, numeric: true, minLength: 8, maxLength: 8, hint: "Eight digits." },
    BANK_NAME,
  ],
  EUR: [
    ACCOUNT_NAME,
    { key: "iban", label: "IBAN", required: true, uppercase: true, minLength: 15, maxLength: 34, placeholder: "DE89 3704 0044 0532 0130 00" },
    { key: "bic", label: "BIC / SWIFT", required: true, uppercase: true, minLength: 8, maxLength: 11 },
    BANK_NAME,
  ],
  USD: [
    ACCOUNT_NAME,
    { key: "accountNumber", label: "Account number", required: true, numeric: true, maxLength: 20 },
    { key: "routingNumber", label: "Routing number (ABA)", required: false, numeric: true, minLength: 9, maxLength: 9, hint: "For a domestic transfer." },
    { key: "swift", label: "SWIFT / BIC", required: false, uppercase: true, minLength: 8, maxLength: 11, hint: "For an international transfer." },
    BANK_NAME,
    { key: "bankAddress", label: "Bank address", required: false, hint: "Some international transfers ask for it." },
  ],
};

/** Anything not listed above. Enough to receive money almost anywhere. */
const FALLBACK: BankFieldSpec[] = [
  ACCOUNT_NAME,
  { key: "accountNumber", label: "Account number", required: true, maxLength: 34 },
  BANK_NAME,
  { key: "swift", label: "SWIFT / BIC", required: false, uppercase: true },
];

export function bankFieldsFor(currency: string): BankFieldSpec[] {
  return BY_CURRENCY[currency.toUpperCase()] ?? FALLBACK;
}

/** Currencies with a purpose-built field list, for the "add an account" picker. */
export function supportedBankCurrencies(): string[] {
  return Object.keys(BY_CURRENCY);
}

/* ------------------------------------------------------------------ account */

export interface BankAccount {
  id: string;
  /** A nickname, so staff can tell two accounts apart. Optional. */
  label: string | null;
  currency: string;
  fields: Record<string, string>;
  /** Only enabled accounts are shown to a payer. */
  enabled: boolean;
  /** A note to the payer — "quote your invoice number", "do not use for service charge". */
  instructions: string | null;
  sortOrder: number;
}

export type BankAccountInput = Pick<BankAccount, "label" | "currency" | "fields" | "enabled" | "instructions">;

export interface BankValidationResult {
  ok: boolean;
  /** Keyed by field key, so a form can put each message under its own input. */
  errors: Record<string, string>;
  /** The values as they should be stored: trimmed, and upper-cased where the spec says so. */
  cleaned: Record<string, string>;
}

/**
 * Validate and normalise one account's fields against its currency's spec.
 *
 * Shared by the form and the API, so the browser and the server cannot disagree about whether
 * an account is complete. Unknown keys are dropped rather than rejected — a currency losing a
 * field should not make existing accounts unsaveable.
 */
export function validateBankAccount(currency: string, fields: Record<string, unknown>): BankValidationResult {
  const spec = bankFieldsFor(currency);
  const errors: Record<string, string> = {};
  const cleaned: Record<string, string> = {};

  for (const field of spec) {
    const raw = fields[field.key];
    let value = typeof raw === "string" ? raw.trim() : "";

    if (field.uppercase) value = value.toUpperCase();
    // Spaces are how people write IBANs and sort codes; strip them for storage and comparison,
    // and put them back for display.
    const compact = field.numeric || field.uppercase ? value.replace(/[\s-]/g, "") : value;

    if (!compact) {
      if (field.required) errors[field.key] = `${field.label} is required`;
      continue;
    }

    if (field.numeric && !/^\d+$/.test(compact)) {
      errors[field.key] = `${field.label} should be digits only`;
      continue;
    }
    if (field.minLength && compact.length < field.minLength) {
      errors[field.key] = `${field.label} should be at least ${field.minLength} characters`;
      continue;
    }
    if (field.maxLength && compact.length > field.maxLength) {
      errors[field.key] = `${field.label} should be at most ${field.maxLength} characters`;
      continue;
    }

    cleaned[field.key] = compact;
  }

  return { ok: Object.keys(errors).length === 0, errors, cleaned };
}

/** IBANs and sort codes are read aloud in groups; store them compact, show them spaced. */
export function formatBankValue(spec: BankFieldSpec, value: string): string {
  if (!value) return "";
  if (spec.key === "iban") return value.replace(/(.{4})/g, "$1 ").trim();
  if (spec.key === "sortCode" && value.length === 6) return `${value.slice(0, 2)}-${value.slice(2, 4)}-${value.slice(4)}`;
  return value;
}

/** The account as label/value pairs, in spec order, skipping anything not filled in. */
export function bankAccountLines(account: Pick<BankAccount, "currency" | "fields">): Array<{ label: string; value: string; key: string }> {
  return bankFieldsFor(account.currency)
    .map((spec) => ({ key: spec.key, label: spec.label, value: formatBankValue(spec, account.fields?.[spec.key] ?? "") }))
    .filter((line) => line.value !== "");
}

/** A one-line summary, for a list row or a receipt footer. */
export function summariseBankAccount(account: Pick<BankAccount, "currency" | "fields" | "label">): string {
  const fields = account.fields ?? {};
  const identifier = fields.accountNumber ?? fields.iban ?? fields.rib ?? "";
  const parts = [fields.accountName, fields.bankName, identifier].filter(Boolean);
  return parts.join(" · ") || account.label || account.currency;
}

/** What a payer sees, in the currency they are paying. Falls back to every enabled account. */
export function accountsForCurrency(accounts: BankAccount[], currency: string): BankAccount[] {
  const enabled = accounts.filter((account) => account.enabled);

  /**
   * Every enabled account is offered — the invoice's own currency first, then the rest.
   *
   * The rule used to hide anything that did not match the invoice. That looked tidy and was
   * wrong twice over: an agency that had deliberately added a foreign account could not see it on
   * its own payment page, and a tenant paying from abroad was offered no way to. An account is on
   * the page because somebody switched it on; the currency decides the order, not the audience.
   */
  const wanted = currency.toUpperCase();
  return [...enabled].sort((a, b) => {
    const aMatches = a.currency.toUpperCase() === wanted ? 0 : 1;
    const bMatches = b.currency.toUpperCase() === wanted ? 0 : 1;
    if (aMatches !== bMatches) return aMatches - bMatches;
    return a.sortOrder - b.sortOrder;
  });
}
